import { MultiSelectOption } from '@dispatch/shared/types';
import { useAccessToken, useCurrentUser } from '@dispatch/web/auth';
import React, { createContext } from 'react';
import { useGetStates } from '../resource';
import { useIsAuthenticated } from '@azure/msal-react';
import { useGetMethodOfInspections } from '../task/UseGetMethodOfInspections';

interface GeneralContextValue {
  statesOptions: MultiSelectOption[];
  methodOfInspectionOptions: MultiSelectOption[];
}

const GeneralContext = createContext<GeneralContextValue | undefined>(
  undefined
);

interface GeneralContextProviderProps {
  children: React.ReactNode;
}

const GeneralDataContextProvider: React.FC<GeneralContextProviderProps> = ({
  children,
}) => {
  const { tenantId } = useCurrentUser();
  const isAuthenticated = useIsAuthenticated();

  const { isAccessTokenSet } = useAccessToken();

  const { states } = useGetStates(
    tenantId || '',
    isAuthenticated && isAccessTokenSet
  );

  const statesOptions: MultiSelectOption[] = React.useMemo(() => {
    return states?.map((state) => ({
      title: state.state,
      value: state.abbreviation,
    }));
  }, [states]);

  const { methodOfInspections } = useGetMethodOfInspections(
    tenantId || '',
    isAuthenticated && isAccessTokenSet
  );

  const methodOfInspectionOptions: MultiSelectOption[] = React.useMemo(() => {
    return methodOfInspections?.map((method) => ({
      title: method,
      value: method.toUpperCase(),
    }));
  }, [methodOfInspections]);

  const value: GeneralContextValue = {
    statesOptions,
    methodOfInspectionOptions,
  };

  return (
    <GeneralContext.Provider value={value}>{children}</GeneralContext.Provider>
  );
};

const useGeneralDataContext = () => {
  const context = React.useContext(GeneralContext);
  if (!context) {
    throw new Error(
      'useGeneralDataContext must be used within a GeneralDataContextProvider'
    );
  }
  return context;
};

export { GeneralContext, useGeneralDataContext, GeneralDataContextProvider };
